/** Upload dimensions are chosen once per renderer, not from user-agent strings.
 * Capability hints are conservative, not a claim about available GPU memory. */
export function skyTextureUploadLimit({
  coarsePointer = false,
  viewportWidth = 0,
  deviceMemory = 0,
} = {}) {
  return coarsePointer ||
    (viewportWidth > 0 && viewportWidth <= 768) ||
    (deviceMemory > 0 && deviceMemory <= 4)
    ? 1024
    : 2048;
}
function canvasFor(width, height) {
  // Some browsers expose OffscreenCanvas without a usable2D context. The
  // ordinary detached canvas is a capability fallback, not a lower upload cap.
  if (typeof OffscreenCanvas === 'function') {
    try {
      const canvas = new OffscreenCanvas(width, height);
      if (canvas.getContext('2d')) return canvas;
      canvas.width = 1;
      canvas.height = 1;
    } catch {
      /* Try the DOM2D canvas below. */
    }
  }
  const canvas = globalThis.document?.createElement?.('canvas');
  if (!canvas) throw new Error('Texture resizing requires a 2D canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}
const cancelled = () => new DOMException('Texture preparation cancelled', 'AbortError');

/** Embedded image work before the first GPU upload. Two decoders share one
 * renderer queue; pending cancellation rejects immediately, late output is
 * disposed, and successful images remain owned by the model cache. Resize only
 * source pixels: GLTF continues to own colorSpace, flipY, UV and sampler setup. */
export function createSkyTextureBudget({
  maxDimension = skyTextureUploadLimit({
    coarsePointer: globalThis.matchMedia?.('(pointer: coarse)')?.matches,
    viewportWidth: globalThis.innerWidth,
    deviceMemory: globalThis.navigator?.deviceMemory,
  }),
  valid = () => true,
  signal,
  makeCanvas = canvasFor,
} = {}) {
  if (![1024, 2048].includes(maxDimension)) throw new Error('Unsupported texture upload budget');
  const queue = [],
    jobs = new Set(),
    canvases = new Set(),
    released = new WeakSet();
  let active = 0,
    dead = false;
  function releaseImage(image) {
    if (!image || typeof image !== 'object' || released.has(image)) return;
    released.add(image);
    if (canvases.delete(image)) {
      image.width = 1;
      image.height = 1;
    } else image.close?.();
  }
  function discard(texture) {
    releaseImage(texture?.source?.data);
    texture?.dispose?.();
  }
  function resize(texture) {
    const source = texture.source,
      image = source?.data;
    const width = image?.naturalWidth || image?.width,
      height = image?.naturalHeight || image?.height;
    // Texture-only test decoders have no pixels. Real decoded images have both.
    if (!width || !height || Math.max(width, height) <= maxDimension) return texture;
    const scale = maxDimension / Math.max(width, height),
      canvas = makeCanvas(
        Math.max(1, Math.round(width * scale)),
        Math.max(1, Math.round(height * scale)),
      ),
      context = canvas.getContext('2d', { alpha: true, colorSpace: 'srgb' });
    if (!context) {
      canvas.width = 1;
      canvas.height = 1;
      throw new Error('Texture resize context unavailable');
    }
    try {
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
    } catch (error) {
      canvas.width = 1;
      canvas.height = 1;
      throw error;
    }
    canvases.add(canvas);
    source.data = canvas;
    texture.needsUpdate = true;
    releaseImage(image);
    return texture;
  }
  function finish(job, error, value) {
    if (job.settled) return;
    job.settled = true;
    job.signal?.removeEventListener('abort', job.abort);
    if (error) job.reject(error);
    else job.resolve(value);
  }
  function pump() {
    while (!dead && active < 2 && queue.length) {
      const job = queue.shift();
      if (job.settled) {
        jobs.delete(job);
        continue;
      }
      if (!valid() || job.signal?.aborted) {
        finish(job, cancelled());
        jobs.delete(job);
        continue;
      }
      active++;
      Promise.resolve()
        .then(() => {
          if (dead || job.settled || !valid() || job.signal?.aborted) throw cancelled();
          return job.load();
        })
        .then(
          (texture) => {
            if (dead || job.settled || !valid() || job.signal?.aborted) {
              discard(texture);
              finish(job, cancelled());
              return;
            }
            try {
              finish(job, null, resize(texture));
            } catch (error) {
              discard(texture);
              finish(job, error);
            }
          },
          (error) => finish(job, error),
        )
        .finally(() => {
          active--;
          jobs.delete(job);
          pump();
        });
    }
  }
  function decode(load, decodeSignal) {
    if (dead || !valid() || decodeSignal?.aborted) return Promise.reject(cancelled());
    return new Promise((resolve, reject) => {
      const job = { load, resolve, reject, signal: decodeSignal, settled: false, abort: null };
      job.abort = () => {
        finish(job, cancelled());
        pump();
      };
      decodeSignal?.addEventListener('abort', job.abort, { once: true });
      jobs.add(job);
      queue.push(job);
      pump();
    });
  }
  function dispose() {
    if (dead) return;
    dead = true;
    signal?.removeEventListener('abort', dispose);
    for (const job of jobs) finish(job, cancelled());
    queue.length = 0;
    jobs.clear();
    for (const canvas of [...canvases]) releaseImage(canvas);
  }
  signal?.addEventListener('abort', dispose, { once: true });
  if (signal?.aborted) dispose();
  return { decode, releaseImage, dispose, maxDimension };
}
